import { createContext } from "react";
import type { KeyboardEventHandler, MouseEventHandler } from "react";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { FileVersionData } from "@Types/fileOperations";
import type { ModalEntry } from "@Reducers/Viewer";
import type { PortalUnits } from "@Actions/Viewer";
import type HoopsViewerImpl from "./ViewerImpl";

interface HoopsViewerContextType {
  /**
   * files.versions._id of the file open in the viewer (24-hex; `fileVersionId` on every request
   * schema). Absent when no file is mounted — i.e. outside a Provider, and on the default context.
   */
  fileVersionId?: ObjectIdString;
  /**
   * The open file's version document (`getFileVersionData`); `null` until it has been fetched.
   * `Partial` because VersionContent mounts a Provider with a file-manager row that carries only
   * some of these members.
   */
  fileVersionData?: Partial<FileVersionData> | null;
  /**
   * The live viewer wrapper that owns `hwv`. Exposed through `HoopsViewerImpl`'s dynamic
   * `[key: string]: any` index signature only: its three typed managers (`_operatorManager`,
   * `_noteTextManager`, `_drawingManimulator`) are omitted here because BottomToolbar, outside
   * this module, dereferences `_operatorManager` unguarded and reaches a private HOOPS member
   * through it, so surfacing their real types means editing viewer-toolbar.
   */
  viewerImpl: Omit<HoopsViewerImpl, "_operatorManager" | "_noteTextManager" | "_drawingManimulator"> | null;
  isModelStructureReady: boolean;
  isSettingsApplied: boolean;
  socketDisconnected: boolean;
  /** Same shape as the `HoopsModalStatus` redux slice: dock panel id -> open/stage state. */
  HoopsModalStatus: Record<string, ModalEntry>;
  /** Issues._id -> selected, as maintained by IssuesPallet. */
  selectedIssues: Record<string, boolean>;
  /** Issues._id of the issue open in the viewer (24-hex; `issueId` on every issues request schema). */
  currentViewIssueId: ObjectIdString | null | undefined;
  /** transmittals.versions._id (24-hex; `transmittalVersionId` on the issues request schemas). */
  transmittalVersionId: ObjectIdString | null | undefined;
  portalUnits?: PortalUnits;
  /** Bound to the `#viewerContainer` div in ViewerContainer. */
  handleClick?: MouseEventHandler<HTMLDivElement>;
  handleKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  /** Whether the quantity-takeoff toolbar is currently shown. */
  quantityTakeoffToolbar?: boolean;
}

export const HoopsViewerContext = createContext<HoopsViewerContextType>({
  // No file is mounted outside a Provider.
  fileVersionId: undefined,
  viewerImpl: null,
  isModelStructureReady: false,
  isSettingsApplied: false,
  socketDisconnected: false,
  HoopsModalStatus: {},
  selectedIssues: {},
  currentViewIssueId: undefined,
  transmittalVersionId: undefined,
  portalUnits: undefined,
});
